"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { Search, SlidersHorizontal, X, ChevronDown, ChevronUp } from "lucide-react"
import type { Product } from "@/lib/types"
import { apiStore } from "@/lib/api-store"
import { ProductCard } from "./product-card"
import { formatPrice, buildFullImageUrl } from "@/lib/utils"
import { useRouter, useSearchParams } from "next/navigation"

interface SearchFilters {
    query: string
    categories: string[]
    minPrice: number
    maxPrice: number
    inStock: boolean | null
    sortBy: "relevance" | "price-asc" | "price-desc" | "sales" | "newest" | "rating"
}

export function AdvancedSearch() {
    const router = useRouter()
    const searchParams = useSearchParams()
    const [products, setProducts] = useState<Product[]>([])
    const [allCategories, setAllCategories] = useState<string[]>([])
    const [loading, setLoading] = useState(true)
    const [showFilters, setShowFilters] = useState(false)
    const [priceRange, setPriceRange] = useState({ min: 0, max: 1000000 })
    const [filters, setFilters] = useState<SearchFilters>({
        query: "",
        categories: [],
        minPrice: 0,
        maxPrice: 1000000,
        inStock: null,
        sortBy: "relevance",
    })

    // Autocomplétion
    const [suggestions, setSuggestions] = useState<Product[]>([])
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [selectedSuggestion, setSelectedSuggestion] = useState(-1)
    const [loadingSuggestions, setLoadingSuggestions] = useState(false)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const suggestionsRef = useRef<HTMLDivElement>(null)
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

    // Sections de filtres repliables
    const [expandedSections, setExpandedSections] = useState({
        categories: true,
        price: true,
        stock: true,
    })

    // Charger les catégories au démarrage
    useEffect(() => {
        loadCategories()
    }, [])

    // Initialiser les filtres depuis l'URL
    useEffect(() => {
        const queryParam = searchParams.get('q')
        const categoryParam = searchParams.get('category')

        if (queryParam) {
            // Vérifier si le paramètre q est une catégorie
            if (allCategories.includes(queryParam)) {
                setFilters(prev => ({
                    ...prev,
                    categories: [queryParam],
                    query: ""
                }))
            } else {
                // C'est une recherche textuelle
                setFilters(prev => ({
                    ...prev,
                    query: queryParam
                }))
            }
        } else if (categoryParam && allCategories.includes(categoryParam)) {
            setFilters(prev => ({
                ...prev,
                categories: [categoryParam],
                query: ""
            }))
        }
    }, [searchParams, allCategories])

    const loadCategories = async () => {
        try {
            const data = await apiStore.getProducts()
            const uniqueCategories = Array.from(new Set(data.map((p: Product) => p.category)))
            setAllCategories(uniqueCategories)

            // Calculer plage de prix
            const prices = data.map((p: Product) => p.price)
            const minPrice = Math.min(...prices)
            const maxPrice = Math.max(...prices)
            setPriceRange({ min: minPrice, max: maxPrice })
            setFilters(prev => ({ ...prev, maxPrice }))
        } catch (error) {
            console.error("Erreur chargement catégories:", error)
        }
    }

    // Recherche avec l'API (debounced pour l'autocomplétion)
    useEffect(() => {
        if (filters.query.length >= 2) {
            // Nettoyer le timer précédent
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }

            setLoadingSuggestions(true)

            debounceTimerRef.current = setTimeout(async () => {
                try {
                    const result = await apiStore.searchProducts(filters.query, 1, 5)
                    setSuggestions(result.products)
                    setShowSuggestions(result.products.length > 0)
                } catch (error) {
                    console.error("Erreur recherche suggestions:", error)
                    setSuggestions([])
                } finally {
                    setLoadingSuggestions(false)
                }
            }, 300) // Debounce de 300ms
        } else {
            setSuggestions([])
            setShowSuggestions(false)
            setLoadingSuggestions(false)
        }
        setSelectedSuggestion(-1)

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [filters.query])

    // Fermer les suggestions quand on clique en dehors
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target as Node) &&
                searchInputRef.current &&
                !searchInputRef.current.contains(event.target as Node)
            ) {
                setShowSuggestions(false)
            }
        }

        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    // Gérer la navigation au clavier dans les suggestions
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showSuggestions || suggestions.length === 0) return

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault()
                setSelectedSuggestion((prev) =>
                    prev < suggestions.length - 1 ? prev + 1 : prev
                )
                break
            case "ArrowUp":
                e.preventDefault()
                setSelectedSuggestion((prev) => (prev > 0 ? prev - 1 : -1))
                break
            case "Enter":
                e.preventDefault()
                if (selectedSuggestion >= 0) {
                    selectSuggestion(suggestions[selectedSuggestion])
                }
                break
            case "Escape":
                setShowSuggestions(false)
                setSelectedSuggestion(-1)
                break
        }
    }

    // Sélectionner une suggestion
    const selectSuggestion = (product: Product) => {
        setShowSuggestions(false)
        setSelectedSuggestion(-1)
        // Rediriger vers la page de détails du produit
        if (product._id || product.id) {
            router.push(`/produits/${product._id || product.id}`)
        }
    }

    // Recherche principale avec filtres
    const performSearch = useCallback(async () => {
        try {
            setLoading(true)
            let results: Product[] = []

            // Recherche par texte via API si query non vide
            if (filters.query.trim()) {
                const searchResult = await apiStore.searchProducts(filters.query, 1, 100)
                results = searchResult.products
            } else {
                // Charger tous les produits si pas de recherche
                results = await apiStore.getProducts()
            }

            // Appliquer les filtres locaux (catégories, prix, stock)
            let filtered = results

            // Filtre par catégories
            if (filters.categories.length > 0) {
                filtered = filtered.filter((p) => filters.categories.includes(p.category))
            }

            // Filtre par prix
            filtered = filtered.filter(
                (p) => p.price >= filters.minPrice && p.price <= filters.maxPrice
            )

            // Filtre par stock
            if (filters.inStock !== null) {
                if (filters.inStock) {
                    filtered = filtered.filter((p) => p.stock && p.stock > 0)
                } else {
                    filtered = filtered.filter((p) => !p.stock || p.stock === 0)
                }
            }

            // Tri
            switch (filters.sortBy) {
                case "price-asc":
                    filtered.sort((a, b) => a.price - b.price)
                    break
                case "price-desc":
                    filtered.sort((a, b) => b.price - a.price)
                    break
                case "sales":
                    filtered.sort((a, b) => (b.sales || 0) - (a.sales || 0))
                    break
                case "newest":
                    filtered.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
                    break
                case "rating":
                    filtered.sort((a, b) => (b.likes || 0) - (a.likes || 0))
                    break
                default:
                    // Pertinence (ordre par défaut de l'API)
                    break
            }

            setProducts(filtered)
        } catch (error) {
            console.error("Erreur lors de la recherche:", error)
            setProducts([])
        } finally {
            setLoading(false)
        }
    }, [filters])

    // Lancer la recherche quand les filtres changent
    useEffect(() => {
        performSearch()
    }, [performSearch])

    // Réinitialiser les filtres
    const resetFilters = () => {
        setFilters({
            query: "",
            categories: [],
            minPrice: priceRange.min,
            maxPrice: priceRange.max,
            inStock: null,
            sortBy: "relevance",
        })
    }

    // Nombre de filtres actifs
    const activeFiltersCount =
        filters.categories.length +
        (filters.inStock !== null ? 1 : 0) +
        (filters.minPrice > priceRange.min || filters.maxPrice < priceRange.max ? 1 : 0)

    const toggleSection = (section: keyof typeof expandedSections) => {
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
    }

    return (
        <div className="space-y-6">
            {/* Barre de recherche principale */}
            <div className="space-y-4">
                <div className="flex gap-3">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Rechercher un produit par nom, description ou catégorie..."
                            value={filters.query}
                            onChange={(e) => setFilters({ ...filters, query: e.target.value })}
                            onKeyDown={handleKeyDown}
                            onFocus={() => {
                                if (suggestions.length > 0 && filters.query.length >= 2) {
                                    setShowSuggestions(true)
                                }
                            }}
                            className="input-field w-full pl-10 h-12 text-base"
                            autoComplete="off"
                        />

                        {/* Dropdown d'autocomplétion */}
                        {showSuggestions && suggestions.length > 0 && (
                            <div
                                ref={suggestionsRef}
                                className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-80 overflow-y-auto"
                            >
                                <div className="p-2">
                                    <div className="text-xs text-gray-500 dark:text-gray-400 px-3 py-2 font-semibold">
                                        Suggestions ({suggestions.length})
                                    </div>
                                    {suggestions.map((product, index) => (
                                        <button
                                            key={product._id || product.id}
                                            onClick={() => selectSuggestion(product)}
                                            className={`w-full text-left px-3 py-3 rounded-lg transition-colors flex items-center gap-3 ${index === selectedSuggestion
                                                ? "bg-primary/10 text-primary"
                                                : "hover:bg-gray-100 dark:hover:bg-gray-800"
                                                }`}
                                        >
                                            {/* Image du produit */}
                                            {product.images && product.images.length > 0 ? (
                                                <img
                                                    src={buildFullImageUrl(product.images[0]) ?? "/placeholder.svg"}
                                                    alt={product.name}
                                                    className="w-12 h-12 object-cover rounded"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center">
                                                    <Search size={20} className="text-gray-400" />
                                                </div>
                                            )}

                                            {/* Infos du produit */}
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium truncate">{product.name}</div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                                                    <span className="truncate">{product.category}</span>
                                                    <span>•</span>
                                                    <span className="font-semibold text-primary">
                                                        {formatPrice(product.price)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Badge de stock */}
                                            {product.stock !== null && product.stock !== undefined && (
                                                <div
                                                    className={`text-xs px-2 py-1 rounded ${product.stock > 0
                                                        ? "bg-success/10 text-success"
                                                        : "bg-danger/10 text-danger"
                                                        }`}
                                                >
                                                    {product.stock > 0 ? `${product.stock} en stock` : "Épuisé"}
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>

                                {/* Astuce navigation clavier */}
                                <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
                                    💡 Utilisez ↑↓ pour naviguer, Entrée pour sélectionner, Échap pour fermer
                                </div>
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowFilters(!showFilters)}
                        className={`h-12 px-6 rounded-lg font-medium transition-all flex items-center gap-2 ${showFilters
                            ? "bg-primary text-white"
                            : "bg-background-secondary hover:bg-background-secondary/80"
                            }`}
                    >
                        <SlidersHorizontal size={20} />
                        Filtres
                        {activeFiltersCount > 0 && (
                            <span className="bg-white text-primary rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                                {activeFiltersCount}
                            </span>
                        )}
                    </button>
                </div>

                {/* Barre de filtres actifs */}
                {activeFiltersCount > 0 && (
                    <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-sm text-foreground-secondary">Filtres actifs :</span>
                        {filters.categories.map((cat) => (
                            <span
                                key={cat}
                                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium"
                            >
                                {cat}
                                <button
                                    onClick={() =>
                                        setFilters({
                                            ...filters,
                                            categories: filters.categories.filter((c) => c !== cat),
                                        })
                                    }
                                    className="hover:text-danger transition-colors"
                                >
                                    <X size={14} />
                                </button>
                            </span>
                        ))}
                        {filters.inStock !== null && (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                                {filters.inStock ? "En stock" : "Épuisé"}
                                <button
                                    onClick={() => setFilters({ ...filters, inStock: null })}
                                    className="hover:text-danger transition-colors"
                                >
                                    <X size={14} />
                                </button>
                            </span>
                        )}
                        {(filters.minPrice > priceRange.min || filters.maxPrice < priceRange.max) && (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                                {formatPrice(filters.minPrice)} - {formatPrice(filters.maxPrice)}
                                <button
                                    onClick={() =>
                                        setFilters({ ...filters, minPrice: priceRange.min, maxPrice: priceRange.max })
                                    }
                                    className="hover:text-danger transition-colors"
                                >
                                    <X size={14} />
                                </button>
                            </span>
                        )}
                        <button
                            onClick={resetFilters}
                            className="text-sm text-primary hover:underline font-medium"
                        >
                            Réinitialiser tout
                        </button>
                    </div>
                )}
            </div>

            <div className="flex gap-6">
                {/* Sidebar de filtres */}
                {showFilters && (
                    <aside className="w-80 space-y-4 shrink-0">
                        <div className="bg-background dark:bg-dark-background rounded-lg border border-border dark:border-dark-border p-4 space-y-4 sticky top-20">
                            {/* Catégories */}
                            <div>
                                <button
                                    onClick={() => toggleSection("categories")}
                                    className="flex items-center justify-between w-full font-semibold text-lg mb-3 hover:text-primary transition-colors"
                                >
                                    <span>Catégories</span>
                                    {expandedSections.categories ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                </button>
                                {expandedSections.categories && (
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                        {allCategories.map((category: string) => (
                                            <label
                                                key={category}
                                                className="flex items-center space-x-2 cursor-pointer hover:bg-background-secondary p-2 rounded transition-colors"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={filters.categories.includes(category)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setFilters({
                                                                ...filters,
                                                                categories: [...filters.categories, category],
                                                            })
                                                        } else {
                                                            setFilters({
                                                                ...filters,
                                                                categories: filters.categories.filter((c) => c !== category),
                                                            })
                                                        }
                                                    }}
                                                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                />
                                                <span className="text-sm">{category}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="h-px bg-border dark:bg-dark-border" />

                            {/* Prix */}
                            <div>
                                <button
                                    onClick={() => toggleSection("price")}
                                    className="flex items-center justify-between w-full font-semibold text-lg mb-3 hover:text-primary transition-colors"
                                >
                                    <span>Plage de prix</span>
                                    {expandedSections.price ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                </button>
                                {expandedSections.price && (
                                    <div className="space-y-4">
                                        <div className="flex gap-3">
                                            <div className="flex-1">
                                                <label className="text-xs text-foreground-secondary mb-1 block">Min</label>
                                                <input
                                                    type="number"
                                                    value={filters.minPrice}
                                                    onChange={(e) =>
                                                        setFilters({ ...filters, minPrice: Number(e.target.value) })
                                                    }
                                                    className="input-field w-full h-9 text-sm"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <label className="text-xs text-foreground-secondary mb-1 block">Max</label>
                                                <input
                                                    type="number"
                                                    value={filters.maxPrice}
                                                    onChange={(e) =>
                                                        setFilters({ ...filters, maxPrice: Number(e.target.value) })
                                                    }
                                                    className="input-field w-full h-9 text-sm"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex justify-between text-xs text-foreground-secondary">
                                            <span>{formatPrice(filters.minPrice)}</span>
                                            <span>{formatPrice(filters.maxPrice)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="h-px bg-border dark:bg-dark-border" />

                            {/* Stock */}
                            <div>
                                <button
                                    onClick={() => toggleSection("stock")}
                                    className="flex items-center justify-between w-full font-semibold text-lg mb-3 hover:text-primary transition-colors"
                                >
                                    <span>Disponibilité</span>
                                    {expandedSections.stock ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                </button>
                                {expandedSections.stock && (
                                    <div className="space-y-2">
                                        <label className="flex items-center space-x-2 cursor-pointer hover:bg-background-secondary p-2 rounded transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={filters.inStock === true}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, inStock: e.target.checked ? true : null })
                                                }
                                                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                            />
                                            <span className="text-sm">En stock uniquement</span>
                                        </label>
                                        <label className="flex items-center space-x-2 cursor-pointer hover:bg-background-secondary p-2 rounded transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={filters.inStock === false}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, inStock: e.target.checked ? false : null })
                                                }
                                                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                            />
                                            <span className="text-sm">Rupture de stock</span>
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>
                    </aside>
                )}

                {/* Résultats */}
                <div className="flex-1 space-y-4">
                    {/* En-tête des résultats avec tri */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <p className="text-foreground-secondary">
                            {loading ? (
                                "Chargement..."
                            ) : (
                                <>
                                    <span className="font-semibold text-foreground">
                                        {products.length}
                                    </span>{" "}
                                    résultat{products.length > 1 ? "s" : ""} trouvé{products.length > 1 ? "s" : ""}
                                </>
                            )}
                        </p>
                        <select
                            value={filters.sortBy}
                            onChange={(e) =>
                                setFilters({ ...filters, sortBy: e.target.value as SearchFilters["sortBy"] })
                            }
                            className="input-field w-full sm:w-48 h-10 text-sm"
                        >
                            <option value="relevance">Pertinence</option>
                            <option value="price-asc">Prix croissant</option>
                            <option value="price-desc">Prix décroissant</option>
                            <option value="sales">Les plus vendus</option>
                            <option value="newest">Nouveautés</option>
                            <option value="rating">Meilleures notes</option>
                        </select>
                    </div>

                    {/* Grille de produits */}
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {[...Array(8)].map((_, i) => (
                                <div key={i} className="animate-pulse">
                                    <div className="bg-background-secondary h-64 rounded-lg" />
                                </div>
                            ))}
                        </div>
                    ) : products.length === 0 ? (
                        <div className="text-center py-12">
                            <Search size={64} className="mx-auto text-foreground-secondary mb-4" />
                            <h3 className="text-xl font-semibold mb-2">
                                Aucun produit trouvé
                            </h3>
                            <p className="text-foreground-secondary mb-4">
                                Essayez de modifier vos critères de recherche
                            </p>
                            <button onClick={resetFilters} className="btn-primary">
                                Réinitialiser les filtres
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {products.map((product) => (
                                <ProductCard key={product._id || product.id} product={product} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
